import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Can } from '../../Components/Can'
import { Button } from '../../Components/Form/Button/button.scomponent'
import { Loader } from '../../Components/Loader'
import { Placeholder } from '../../Components/Placeholder/placeholder.component'
import { BaseContentPage } from '../../Components/Templates/BaseContentPage/base-content-page.componen'
import { BaseScreen } from '../../Components/Templates/BaseScreen/basescreen.component'
import { useToast } from '../../Hooks/ToasContext'
import api from '../../Services/api'
import { maskCNPJ } from '../../utils/maskCNPJ'

type PaymentType = 'DEFAULT' | 'TICKET' | 'PIX' | 'CREDIT_CARD'
type Acquirer = 'BB' | 'ITAU' | 'SICOOB' | 'SANTANDER' | 'IPAG' | 'CIELO' | 'REDE'
type ScenarioType = 'PRODUCT_SHIPPING' | 'PRODUCT_SALES' | 'BONUS' | 'SUFRAMA' | 'COMMODATUM'
type Channel = 'NUVEMSHOP'
type StoreType = 'B2B' | 'B2C'

interface OmieCompany {
  id: string
  name: string
  alias: string
  cnpj?: string | null
  active: boolean
  canIssueInvoice: boolean
  canReceivePayment: boolean
  encryptionKeyVersion?: string | null
  settings?: Record<string, unknown>
  hasCredentials?: boolean
}

interface BankAccount {
  id: string
  paymentType: PaymentType
  acquirer?: Acquirer | null
  omieBankAccountId: string
  name: string
  priority: number
  active: boolean
}

interface FiscalScenario {
  id: string
  scenarioType: ScenarioType
  omieFiscalScenarioId: number | string
  active: boolean
}

interface OrderOrigin {
  id: string
  name: string
  channel: Channel
  externalStoreId: string
  storeType: StoreType
  defaultBillingCompanyId: string
  defaultReceivingCompanyId: string | null
  active: boolean
}

interface CarrierOmieCompany {
  id: string
  carrierId?: string
  carrier?: Carrier
  omieCompanyId: string
  omieCompany?: {
    id: string
    name: string
    alias?: string
  }
  omieCarrierId: string
  active: boolean
}

interface Carrier {
  id: string
  name: string
  slug?: string
  intelipostCodeId?: string | null
  logoUrl?: string | null
  isActive: boolean
  b2bOmieCarrierId?: string | number | null
  b2cOmieCarrierId?: string | number | null
}

interface CarrierCompanyRow {
  id: string
  omieCompanyId: string
  companyName: string
  companyAlias: string
  linkId: string | null
  omieCarrierId: string
  active: boolean
}

interface CompanyForm {
  name: string
  alias: string
  cnpj: string
  active: boolean
  canIssueInvoice: boolean
  canReceivePayment: boolean
  appKey: string
  appSecret: string
  clearCredentials: boolean
}

interface BankAccountForm {
  paymentType: PaymentType
  acquirer: Acquirer | ''
  omieBankAccountId: string
  name: string
  priority: string
  active: boolean
}

interface FiscalScenarioForm {
  scenarioType: ScenarioType
  omieFiscalScenarioId: string
  active: boolean
}

interface OrderOriginForm {
  name: string
  channel: Channel
  externalStoreId: string
  storeType: StoreType
  defaultBillingCompanyId: string
  defaultReceivingCompanyId: string
  active: boolean
}

interface CarrierForm {
  name: string
  intelipostCodeId: string
  logoUrl: string
  isActive: boolean
}

const paymentTypeLabels: Record<PaymentType, string> = {
  DEFAULT: 'Padrão',
  TICKET: 'Boleto',
  PIX: 'PIX',
  CREDIT_CARD: 'Cartão',
}

const acquirerLabels: Record<Acquirer, string> = {
  BB: 'Banco do Brasil',
  ITAU: 'Banco Itaú',
  SICOOB: 'Banco Sicoob',
  SANTANDER: 'Santander',
  IPAG: 'IPAG',
  CIELO: 'Cielo',
  REDE: 'Rede',
}

const scenarioTypeLabels: Record<ScenarioType, string> = {
  PRODUCT_SHIPPING: 'Remessa',
  PRODUCT_SALES: 'Venda/produto',
  BONUS: 'Bonificação',
  SUFRAMA: 'Suframa',
  COMMODATUM: 'Comodato',
}

const paymentTypes = Object.keys(paymentTypeLabels) as PaymentType[]
const acquirers = Object.keys(acquirerLabels) as Acquirer[]
const scenarioTypes = Object.keys(scenarioTypeLabels) as ScenarioType[]

const emptyCompanyForm: CompanyForm = {
  name: '',
  alias: '',
  cnpj: '',
  active: true,
  canIssueInvoice: true,
  canReceivePayment: true,
  appKey: '',
  appSecret: '',
  clearCredentials: false,
}

const emptyBankAccountForm: BankAccountForm = {
  paymentType: 'DEFAULT',
  acquirer: '',
  omieBankAccountId: '',
  name: '',
  priority: '0',
  active: true,
}

const emptyFiscalScenarioForm: FiscalScenarioForm = {
  scenarioType: 'PRODUCT_SALES',
  omieFiscalScenarioId: '',
  active: true,
}

const emptyOrderOriginForm: OrderOriginForm = {
  name: '',
  channel: 'NUVEMSHOP',
  externalStoreId: '',
  storeType: 'B2B',
  defaultBillingCompanyId: '',
  defaultReceivingCompanyId: '',
  active: true,
}

const emptyCarrierForm: CarrierForm = {
  name: '',
  intelipostCodeId: '',
  logoUrl: '',
  isActive: true,
}

function normalizeRows<T>(response: unknown): T[] {
  if (Array.isArray(response)) return response as T[]

  const data = response as { data?: T[]; items?: T[]; results?: T[]; rows?: T[] }
  return data?.data ?? data?.items ?? data?.results ?? data?.rows ?? []
}

function getErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.error
    ?? error?.response?.data?.message
    ?? error?.message
    ?? fallback
}

function booleanChip(value: boolean, trueLabel = 'Sim', falseLabel = 'Não') {
  return (
    <Chip
      label={value ? trueLabel : falseLabel}
      size="small"
      color={value ? 'success' : 'default'}
      variant={value ? 'filled' : 'outlined'}
    />
  )
}

function selectedCompanyName(companies: OmieCompany[], companyId: string | null | undefined) {
  if (!companyId) return 'Padrão'
  return companies.find(company => company.id === companyId)?.name ?? companyId
}

const OmieCompaniesAdmin: FC = () => {
  const { showToast } = useToast()
  const [tabIndex, setTabIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const [companies, setCompanies] = useState<OmieCompany[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState('')
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([])
  const [fiscalScenarios, setFiscalScenarios] = useState<FiscalScenario[]>([])
  const [orderOrigins, setOrderOrigins] = useState<OrderOrigin[]>([])
  const [carriers, setCarriers] = useState<Carrier[]>([])
  const [carrierLinks, setCarrierLinks] = useState<CarrierOmieCompany[]>([])
  const [selectedCarrierId, setSelectedCarrierId] = useState('')
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false)
  const [bankDialogOpen, setBankDialogOpen] = useState(false)
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false)
  const [originDialogOpen, setOriginDialogOpen] = useState(false)
  const [carrierDialogOpen, setCarrierDialogOpen] = useState(false)
  const [editingCompany, setEditingCompany] = useState<OmieCompany | null>(null)
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null)
  const [editingFiscalScenario, setEditingFiscalScenario] = useState<FiscalScenario | null>(null)
  const [editingOrderOrigin, setEditingOrderOrigin] = useState<OrderOrigin | null>(null)
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null)
  const [companyForm, setCompanyForm] = useState<CompanyForm>(emptyCompanyForm)
  const [bankAccountForm, setBankAccountForm] = useState<BankAccountForm>(emptyBankAccountForm)
  const [fiscalScenarioForm, setFiscalScenarioForm] = useState<FiscalScenarioForm>(emptyFiscalScenarioForm)
  const [orderOriginForm, setOrderOriginForm] = useState<OrderOriginForm>(emptyOrderOriginForm)
  const [carrierForm, setCarrierForm] = useState<CarrierForm>(emptyCarrierForm)
  const [carrierRows, setCarrierRows] = useState<CarrierCompanyRow[]>([])

  const selectedCompany = useMemo(
    () => companies.find(company => company.id === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  )

  const activeBillingCompanies = useMemo(
    () => companies.filter(company => company.active && company.canIssueInvoice),
    [companies],
  )

  const activeReceivingCompanies = useMemo(
    () => companies.filter(company => company.active && company.canReceivePayment),
    [companies],
  )

  const activeCarrierCompanies = useMemo(
    () => companies.filter(company => company.active && company.canIssueInvoice),
    [companies],
  )

  const selectedCarrier = useMemo(
    () => carriers.find(carrier => carrier.id === selectedCarrierId) ?? null,
    [carriers, selectedCarrierId],
  )

  const loadCompanies = useCallback(async () => {
    const response = await api.get('/backoffice/omie-companies')
    const rows = normalizeRows<OmieCompany>(response.data)
    setCompanies(rows)
    setSelectedCompanyId(current => current || rows[0]?.id || '')
  }, [])

  const loadCompanyChildren = useCallback(async (companyId: string) => {
    if (!companyId) {
      setBankAccounts([])
      setFiscalScenarios([])
      return
    }

    const [bankResponse, scenariosResponse] = await Promise.all([
      api.get(`/backoffice/omie-companies/${companyId}/bank-accounts`),
      api.get(`/backoffice/omie-companies/${companyId}/fiscal-scenarios`),
    ])

    setBankAccounts(normalizeRows<BankAccount>(bankResponse.data))
    setFiscalScenarios(normalizeRows<FiscalScenario>(scenariosResponse.data))
  }, [])

  const loadOrderOrigins = useCallback(async () => {
    const response = await api.get('/backoffice/order-origins')
    setOrderOrigins(normalizeRows<OrderOrigin>(response.data))
  }, [])

  const loadCarriers = useCallback(async () => {
    const response = await api.get('/carriers', {
      params: {
        page: 1,
        offset: 100,
      },
    })
    const rows = normalizeRows<Carrier>(response.data)
    setCarriers(rows)
    setSelectedCarrierId(current => current || rows[0]?.id || '')
  }, [])

  const loadCarrierLinks = useCallback(async () => {
    if (!selectedCarrierId) {
      setCarrierLinks([])
      return
    }

    const response = await api.get(`/backoffice/carriers/${selectedCarrierId}/omie-companies`)
    setCarrierLinks(normalizeRows<CarrierOmieCompany>(response.data))
  }, [selectedCarrierId])

  const loadAll = useCallback(async () => {
    try {
      setLoading(true)
      setLoadError(false)
      await Promise.all([loadCompanies(), loadOrderOrigins(), loadCarriers()])
    } catch (error) {
      setLoadError(true)
      showToast(getErrorMessage(error, 'Erro ao carregar cadastro Omie'), 'error')
    } finally {
      setLoading(false)
    }
  }, [loadCarriers, loadCompanies, loadOrderOrigins, showToast])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  useEffect(() => {
    if (tabIndex !== 4) return

    const load = async () => {
      try {
        setLoading(true)
        await loadCarrierLinks()
      } catch (error) {
        showToast(getErrorMessage(error, 'Erro ao carregar vínculos da transportadora'), 'error')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [loadCarrierLinks, showToast, tabIndex])

  useEffect(() => {
    const rows = activeCarrierCompanies.map(company => {
      const link = carrierLinks.find(item => item.omieCompanyId === company.id)

      return {
        id: company.id,
        omieCompanyId: company.id,
        companyName: company.name,
        companyAlias: company.alias,
        linkId: link?.id ?? null,
        omieCarrierId: link?.omieCarrierId ?? '',
        active: link?.active ?? true,
      }
    })

    setCarrierRows(rows)
  }, [activeCarrierCompanies, carrierLinks])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        await loadCompanyChildren(selectedCompanyId)
      } catch (error) {
        showToast(getErrorMessage(error, 'Erro ao carregar dados da empresa'), 'error')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [loadCompanyChildren, selectedCompanyId, showToast])

  const updateCompanyForm = <K extends keyof CompanyForm>(field: K, value: CompanyForm[K]) => {
    setCompanyForm(current => ({ ...current, [field]: value }))
  }

  const updateBankAccountForm = <K extends keyof BankAccountForm>(field: K, value: BankAccountForm[K]) => {
    setBankAccountForm(current => ({ ...current, [field]: value }))
  }

  const updateFiscalScenarioForm = <K extends keyof FiscalScenarioForm>(field: K, value: FiscalScenarioForm[K]) => {
    setFiscalScenarioForm(current => ({ ...current, [field]: value }))
  }

  const updateOrderOriginForm = <K extends keyof OrderOriginForm>(field: K, value: OrderOriginForm[K]) => {
    setOrderOriginForm(current => ({ ...current, [field]: value }))
  }

  const updateCarrierForm = <K extends keyof CarrierForm>(field: K, value: CarrierForm[K]) => {
    setCarrierForm(current => ({ ...current, [field]: value }))
  }

  const updateCarrierRow = (omieCompanyId: string, changes: Partial<CarrierCompanyRow>) => {
    setCarrierRows(current => current.map(row => row.omieCompanyId === omieCompanyId ? { ...row, ...changes } : row))
  }

  const openCompanyDialog = (company?: OmieCompany) => {
    setEditingCompany(company ?? null)
    setCompanyForm(company
      ? {
        name: company.name ?? '',
        alias: company.alias ?? '',
        cnpj: company.cnpj ?? '',
        active: company.active,
        canIssueInvoice: company.canIssueInvoice,
        canReceivePayment: company.canReceivePayment,
        appKey: '',
        appSecret: '',
        clearCredentials: false,
      }
      : emptyCompanyForm)
    setCompanyDialogOpen(true)
  }

  const openBankDialog = (account?: BankAccount) => {
    setEditingBankAccount(account ?? null)
    setBankAccountForm(account
      ? {
        paymentType: account.paymentType,
        acquirer: account.acquirer ?? '',
        omieBankAccountId: account.omieBankAccountId ?? '',
        name: account.name ?? '',
        priority: String(account.priority ?? 0),
        active: account.active,
      }
      : emptyBankAccountForm)
    setBankDialogOpen(true)
  }

  const openScenarioDialog = (scenario?: FiscalScenario) => {
    setEditingFiscalScenario(scenario ?? null)
    setFiscalScenarioForm(scenario
      ? {
        scenarioType: scenario.scenarioType,
        omieFiscalScenarioId: String(scenario.omieFiscalScenarioId ?? ''),
        active: scenario.active,
      }
      : emptyFiscalScenarioForm)
    setScenarioDialogOpen(true)
  }

  const openOriginDialog = (origin?: OrderOrigin) => {
    setEditingOrderOrigin(origin ?? null)
    setOrderOriginForm(origin
      ? {
        name: origin.name ?? '',
        channel: origin.channel,
        externalStoreId: origin.externalStoreId ?? '',
        storeType: origin.storeType,
        defaultBillingCompanyId: origin.defaultBillingCompanyId ?? '',
        defaultReceivingCompanyId: origin.defaultReceivingCompanyId ?? '',
        active: origin.active,
      }
      : {
        ...emptyOrderOriginForm,
        defaultBillingCompanyId: activeBillingCompanies[0]?.id ?? '',
      })
    setOriginDialogOpen(true)
  }

  const openCarrierDialog = (carrier?: Carrier) => {
    setEditingCarrier(carrier ?? null)
    setCarrierForm(carrier
      ? {
        name: carrier.name ?? '',
        intelipostCodeId: carrier.intelipostCodeId ?? '',
        logoUrl: carrier.logoUrl ?? '',
        isActive: carrier.isActive,
      }
      : emptyCarrierForm)
    setCarrierDialogOpen(true)
  }

  const saveCompany = async () => {
    if (!companyForm.name.trim() || !companyForm.alias.trim()) {
      showToast('Nome e alias são obrigatórios', 'warning')
      return
    }

    if ((companyForm.appKey && !companyForm.appSecret) || (companyForm.appSecret && !companyForm.appKey)) {
      showToast('App Key e App Secret devem ser preenchidos juntos', 'warning')
      return
    }

    try {
      setLoading(true)
      const payload: Record<string, unknown> = {
        name: companyForm.name.trim(),
        alias: companyForm.alias.trim().toUpperCase(),
        cnpj: companyForm.cnpj.replace(/\D/g, ''),
        active: companyForm.active,
        canIssueInvoice: companyForm.canIssueInvoice,
        canReceivePayment: companyForm.canReceivePayment,
        settings: editingCompany?.settings ?? {},
        clearCredentials: companyForm.clearCredentials,
      }

      if (companyForm.appKey || companyForm.appSecret) {
        payload.appKey = companyForm.appKey
        payload.appSecret = companyForm.appSecret
      }

      if (editingCompany) {
        await api.put(`/backoffice/omie-companies/${editingCompany.id}`, payload)
      } else {
        await api.post('/backoffice/omie-companies', payload)
      }

      showToast(editingCompany ? 'Empresa atualizada' : 'Empresa cadastrada', 'success')
      setCompanyDialogOpen(false)
      await loadCompanies()
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao salvar empresa'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const saveBankAccount = async () => {
    if (!selectedCompanyId) {
      showToast('Selecione uma empresa', 'warning')
      return
    }

    if (!bankAccountForm.paymentType || !bankAccountForm.omieBankAccountId.trim() || !bankAccountForm.name.trim()) {
      showToast('Tipo, ID da conta Omie e nome são obrigatórios', 'warning')
      return
    }

    try {
      setLoading(true)
      const payload = {
        paymentType: bankAccountForm.paymentType,
        acquirer: bankAccountForm.acquirer || null,
        omieBankAccountId: bankAccountForm.omieBankAccountId.trim(),
        name: bankAccountForm.name.trim(),
        priority: Number(bankAccountForm.priority || 0),
        active: bankAccountForm.active,
      }

      if (editingBankAccount) {
        await api.put(`/backoffice/omie-company-bank-accounts/${editingBankAccount.id}`, payload)
      } else {
        await api.post(`/backoffice/omie-companies/${selectedCompanyId}/bank-accounts`, payload)
      }

      showToast(editingBankAccount ? 'Conta bancária atualizada' : 'Conta bancária cadastrada', 'success')
      setBankDialogOpen(false)
      await loadCompanyChildren(selectedCompanyId)
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao salvar conta bancária'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const saveFiscalScenario = async () => {
    if (!selectedCompanyId) {
      showToast('Selecione uma empresa', 'warning')
      return
    }

    if (!fiscalScenarioForm.scenarioType || !fiscalScenarioForm.omieFiscalScenarioId.trim()) {
      showToast('Tipo de cenário e ID fiscal Omie são obrigatórios', 'warning')
      return
    }

    try {
      setLoading(true)
      const payload = {
        scenarioType: fiscalScenarioForm.scenarioType,
        omieFiscalScenarioId: Number(fiscalScenarioForm.omieFiscalScenarioId),
        active: fiscalScenarioForm.active,
      }

      if (editingFiscalScenario) {
        await api.put(`/backoffice/omie-company-fiscal-scenarios/${editingFiscalScenario.id}`, payload)
      } else {
        await api.post(`/backoffice/omie-companies/${selectedCompanyId}/fiscal-scenarios`, payload)
      }

      showToast(editingFiscalScenario ? 'Cenário fiscal atualizado' : 'Cenário fiscal cadastrado', 'success')
      setScenarioDialogOpen(false)
      await loadCompanyChildren(selectedCompanyId)
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao salvar cenário fiscal'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const saveOrderOrigin = async () => {
    if (
      !orderOriginForm.name.trim()
      || !orderOriginForm.channel
      || !orderOriginForm.externalStoreId.trim()
      || !orderOriginForm.storeType
      || !orderOriginForm.defaultBillingCompanyId
    ) {
      showToast('Nome, canal, loja externa, tipo de loja e empresa de faturamento são obrigatórios', 'warning')
      return
    }

    try {
      setLoading(true)
      const payload = {
        name: orderOriginForm.name.trim(),
        channel: orderOriginForm.channel,
        externalStoreId: orderOriginForm.externalStoreId.trim(),
        storeType: orderOriginForm.storeType,
        defaultBillingCompanyId: orderOriginForm.defaultBillingCompanyId,
        defaultReceivingCompanyId: orderOriginForm.defaultReceivingCompanyId || null,
        active: orderOriginForm.active,
      }

      if (editingOrderOrigin) {
        await api.put(`/backoffice/order-origins/${editingOrderOrigin.id}`, payload)
      } else {
        await api.post('/backoffice/order-origins', payload)
      }

      showToast(editingOrderOrigin ? 'Origem atualizada' : 'Origem cadastrada', 'success')
      setOriginDialogOpen(false)
      await loadOrderOrigins()
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao salvar origem'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const saveCarrier = async () => {
    if (!carrierForm.name.trim() || !carrierForm.intelipostCodeId.trim()) {
      showToast('Nome e código Intelipost são obrigatórios', 'warning')
      return
    }

    try {
      setLoading(true)
      const payload = {
        name: carrierForm.name.trim(),
        intelipostCodeId: carrierForm.intelipostCodeId.trim(),
        logoUrl: carrierForm.logoUrl.trim() || null,
        isActive: carrierForm.isActive,
      }

      if (editingCarrier) {
        await api.put(`/carriers/${editingCarrier.id}`, payload)
      } else {
        await api.post('/carriers', payload)
      }

      showToast(editingCarrier ? 'Transportadora atualizada' : 'Transportadora cadastrada', 'success')
      setCarrierDialogOpen(false)
      await loadCarriers()
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao salvar transportadora'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const saveCarrierCompanyRow = async (row: CarrierCompanyRow) => {
    if (!selectedCarrierId) {
      showToast('Selecione uma transportadora', 'warning')
      return
    }

    if (!row.omieCarrierId.trim()) {
      showToast('Informe o código da transportadora no Omie', 'warning')
      return
    }

    try {
      setLoading(true)

      if (row.linkId) {
        await api.put(`/backoffice/carrier-omie-companies/${row.linkId}`, {
          omieCarrierId: row.omieCarrierId.trim(),
          active: row.active,
        })
      } else {
        await api.post(`/backoffice/carriers/${selectedCarrierId}/omie-companies`, {
          omieCompanyId: row.omieCompanyId,
          omieCarrierId: row.omieCarrierId.trim(),
          active: row.active,
        })
      }

      showToast('Código Omie salvo', 'success')
      await loadCarrierLinks()
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao salvar código Omie da transportadora'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const disableCarrierCompanyRow = async (row: CarrierCompanyRow) => {
    if (!row.linkId) return

    try {
      setLoading(true)
      await api.delete(`/backoffice/carrier-omie-companies/${row.linkId}`)
      showToast('Vínculo desativado', 'success')
      await loadCarrierLinks()
    } catch (error) {
      showToast(getErrorMessage(error, 'Erro ao desativar vínculo da transportadora'), 'error')
    } finally {
      setLoading(false)
    }
  }

  const companyColumns = useMemo<GridColDef[]>(() => [
    { field: 'name', headerName: 'Nome', minWidth: 180, flex: 1 },
    {
      field: 'alias',
      headerName: 'Alias',
      width: 110,
      renderCell: params => <Chip label={params.value || '-'} size="small" variant="outlined" />,
    },
    { field: 'cnpj', headerName: 'CNPJ', minWidth: 150, flex: 0.8, renderCell: params => maskCNPJ(String(params.value ?? '')) || '-' },
    { field: 'active', headerName: 'Ativa', width: 110, renderCell: params => booleanChip(Boolean(params.value), 'Ativa', 'Inativa') },
    { field: 'canIssueInvoice', headerName: 'Emite nota', width: 130, renderCell: params => booleanChip(Boolean(params.value)) },
    { field: 'canReceivePayment', headerName: 'Recebe financeiro', width: 160, renderCell: params => booleanChip(Boolean(params.value)) },
    { field: 'hasCredentials', headerName: 'Credenciais', width: 130, renderCell: params => booleanChip(Boolean(params.value)) },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 130,
      sortable: false,
      renderCell: params => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar empresa">
            <IconButton size="small" onClick={() => openCompanyDialog(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Configurar empresa">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedCompanyId(params.row.id)
                setTabIndex(1)
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ], [])

  const bankAccountColumns = useMemo<GridColDef[]>(() => [
    { field: 'name', headerName: 'Nome', minWidth: 220, flex: 1 },
    { field: 'paymentType', headerName: 'Pagamento', minWidth: 140, flex: 0.8, renderCell: params => paymentTypeLabels[params.value as PaymentType] ?? params.value },
    { field: 'acquirer', headerName: 'Adquirente', minWidth: 140, flex: 0.8, renderCell: params => params.value ? acquirerLabels[params.value as Acquirer] ?? params.value : '-' },
    { field: 'omieBankAccountId', headerName: 'ID Omie', minWidth: 150, flex: 0.8 },
    { field: 'priority', headerName: 'Prioridade', width: 110 },
    { field: 'active', headerName: 'Status', width: 120, renderCell: params => booleanChip(Boolean(params.value), 'Ativa', 'Inativa') },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 90,
      sortable: false,
      renderCell: params => (
        <Tooltip title="Editar conta">
          <IconButton size="small" onClick={() => openBankDialog(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [])

  const fiscalScenarioColumns = useMemo<GridColDef[]>(() => [
    { field: 'scenarioType', headerName: 'Cenário', minWidth: 220, flex: 1, renderCell: params => scenarioTypeLabels[params.value as ScenarioType] ?? params.value },
    { field: 'omieFiscalScenarioId', headerName: 'ID Fiscal Omie', minWidth: 180, flex: 1 },
    { field: 'active', headerName: 'Status', width: 120, renderCell: params => booleanChip(Boolean(params.value), 'Ativo', 'Inativo') },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 90,
      sortable: false,
      renderCell: params => (
        <Tooltip title="Editar cenário">
          <IconButton size="small" onClick={() => openScenarioDialog(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [])

  const orderOriginColumns = useMemo<GridColDef[]>(() => [
    { field: 'name', headerName: 'Nome', minWidth: 190, flex: 1 },
    { field: 'channel', headerName: 'Canal', width: 130 },
    { field: 'externalStoreId', headerName: 'Loja externa', minWidth: 150, flex: 0.8 },
    { field: 'storeType', headerName: 'Tipo loja', width: 120 },
    { field: 'defaultBillingCompanyId', headerName: 'Faturamento', minWidth: 180, flex: 1, renderCell: params => selectedCompanyName(companies, params.value) },
    { field: 'defaultReceivingCompanyId', headerName: 'Recebimento', minWidth: 180, flex: 1, renderCell: params => selectedCompanyName(companies, params.value) },
    { field: 'active', headerName: 'Status', width: 120, renderCell: params => booleanChip(Boolean(params.value), 'Ativa', 'Inativa') },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 90,
      sortable: false,
      renderCell: params => (
        <Tooltip title="Editar origem">
          <IconButton size="small" onClick={() => openOriginDialog(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [companies])

  const carrierRowColumns = useMemo<GridColDef[]>(() => [
    {
      field: 'companyName',
      headerName: 'Empresa Omie',
      minWidth: 220,
      flex: 1,
      renderCell: params => (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
          <Chip label={params.row.companyAlias} size="small" variant="outlined" />
          <Typography fontSize={14}>{params.value}</Typography>
        </Stack>
      ),
    },
    {
      field: 'omieCarrierId',
      headerName: 'Código no Omie',
      minWidth: 220,
      flex: 1,
      sortable: false,
      renderCell: params => (
        <TextField
          size="small"
          fullWidth
          value={params.row.omieCarrierId}
          onChange={(event) => updateCarrierRow(params.row.omieCompanyId, { omieCarrierId: event.target.value })}
        />
      ),
    },
    {
      field: 'active',
      headerName: 'Ativo',
      width: 110,
      sortable: false,
      renderCell: params => (
        <Checkbox
          checked={Boolean(params.row.active)}
          onChange={(event) => updateCarrierRow(params.row.omieCompanyId, { active: event.target.checked })}
        />
      ),
    },
    { field: 'linkId', headerName: 'Status', width: 120, renderCell: params => params.value ? booleanChip(Boolean(params.row.active), 'Vinculado', 'Inativo') : <Chip label="Sem código" size="small" variant="outlined" /> },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 130,
      sortable: false,
      renderCell: params => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Salvar código">
            <IconButton size="small" onClick={() => saveCarrierCompanyRow(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Desativar vínculo">
            <span>
              <IconButton size="small" color="error" disabled={!params.row.linkId || !params.row.active} onClick={() => disableCarrierCompanyRow(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ], [selectedCarrierId])

  const renderGrid = (rows: Array<{ id: string }>, columns: GridColDef[], emptyText: string, onRetry?: () => void) => {
    if (loading && !rows.length) return <Loader />
    if (loadError && onRetry) return <Placeholder description="Não foi possível carregar os dados." onRetry={onRetry} />
    if (!rows.length) return <Placeholder description={emptyText} />

    return (
      <Box sx={{ width: '100%', height: 'calc(100vh - 390px)', minHeight: 420 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={52}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          }}
        />
      </Box>
    )
  }

  const companySelector = (
    <FormControl sx={{ minWidth: { xs: '100%', md: 320 } }}>
      <InputLabel id="omie-company-selector-label">Empresa Omie</InputLabel>
      <Select
        labelId="omie-company-selector-label"
        label="Empresa Omie"
        value={selectedCompanyId}
        onChange={(event) => setSelectedCompanyId(event.target.value as string)}
      >
        {companies.map(company => (
          <MenuItem key={company.id} value={company.id}>
            {company.name} ({company.alias})
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )

  return (
    <Can permissions={['menu:omieCompanies:manage']}>
      <BaseScreen>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
          <BaseContentPage
            title="Cadastro de Empresas Omie"
            subtitle="Configure empresas, contas, cenários fiscais e defaults usados no faturamento dinâmico"
            breadcrumb={[{ title: 'Cadastro de Empresas Omie', link: '/omie-companies' }]}
            minHeight="auto"
          >
            {loading && !!companies.length && <Loader />}

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabIndex} onChange={(event, value) => setTabIndex(value)}>
                <Tab label="Empresas" />
                <Tab label="Contas Bancárias" />
                <Tab label="Cenários Fiscais" />
                <Tab label="Defaults por Origem" />
                <Tab label="Transportadoras" />
              </Tabs>
            </Box>

            {tabIndex === 0 && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {companies.length} empresas cadastradas
                  </Typography>
                  <Button
                    LeftIcon={AddIcon}
                    sx={{ width: { xs: '100%', md: 180 } }}
                    textSx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => openCompanyDialog()}
                  >
                    Nova empresa
                  </Button>
                </Stack>
                {renderGrid(companies, companyColumns, 'Nenhuma empresa Omie encontrada.', loadAll)}
              </Stack>
            )}

            {tabIndex === 1 && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                  {companySelector}
                  <Button
                    LeftIcon={AddIcon}
                    disabled={!selectedCompanyId}
                    sx={{ width: { xs: '100%', md: 210 } }}
                    textSx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => openBankDialog()}
                  >
                    Nova conta
                  </Button>
                </Stack>
                {selectedCompany && (
                  <Alert severity="info">
                    Configurando contas de recebimento para {selectedCompany.name}.
                  </Alert>
                )}
                {renderGrid(bankAccounts, bankAccountColumns, 'Nenhuma conta bancária cadastrada para esta empresa.')}
              </Stack>
            )}

            {tabIndex === 2 && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                  {companySelector}
                  <Button
                    LeftIcon={AddIcon}
                    disabled={!selectedCompanyId}
                    sx={{ width: { xs: '100%', md: 220 } }}
                    textSx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => openScenarioDialog()}
                  >
                    Novo cenário
                  </Button>
                </Stack>
                {selectedCompany && (
                  <Alert severity="info">
                    Configurando cenários fiscais para {selectedCompany.name}.
                  </Alert>
                )}
                {renderGrid(fiscalScenarios, fiscalScenarioColumns, 'Nenhum cenário fiscal cadastrado para esta empresa.')}
              </Stack>
            )}

            {tabIndex === 3 && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    {orderOrigins.length} origens cadastradas
                  </Typography>
                  <Button
                    LeftIcon={AddIcon}
                    sx={{ width: { xs: '100%', md: 190 } }}
                    textSx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => openOriginDialog()}
                  >
                    Nova origem
                  </Button>
                </Stack>
                <Alert severity="info">
                  Use recebimento Padrão para receber na mesma empresa selecionada para faturamento.
                </Alert>
                {renderGrid(orderOrigins, orderOriginColumns, 'Nenhuma origem cadastrada.', loadAll)}
              </Stack>
            )}

            {tabIndex === 4 && (
              <Stack spacing={2}>
                <Grid2 container spacing={2} alignItems="center">
                  <Grid2 size={{ xs: 12, md: 7 }}>
                    <FormControl fullWidth>
                      <InputLabel id="carrier-selector-label">Transportadora HUB</InputLabel>
                      <Select
                        labelId="carrier-selector-label"
                        label="Transportadora HUB"
                        value={selectedCarrierId}
                        onChange={(event) => setSelectedCarrierId(event.target.value as string)}
                      >
                        {carriers.map(carrier => (
                          <MenuItem key={carrier.id} value={carrier.id}>
                            {carrier.name} {carrier.intelipostCodeId ? `- Intelipost ${carrier.intelipostCodeId}` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button
                        variant="outlined"
                        disabled={!selectedCarrierId}
                        onClick={() => selectedCarrier && openCarrierDialog(selectedCarrier)}
                      >
                        Editar
                      </Button>
                      <Button
                        LeftIcon={AddIcon}
                        onClick={() => openCarrierDialog()}
                      >
                        Nova transportadora
                      </Button>
                    </Stack>
                  </Grid2>
                </Grid2>
                {selectedCarrier && (
                  <Alert severity="info">
                    Configure o código Omie de {selectedCarrier.name} para cada empresa de faturamento ativa.
                  </Alert>
                )}
                {selectedCarrier
                  ? renderGrid(carrierRows, carrierRowColumns, 'Nenhuma empresa emissora ativa encontrada.')
                  : <Placeholder description="Selecione ou cadastre uma transportadora para configurar os códigos Omie." />}
              </Stack>
            )}
          </BaseContentPage>
        </Paper>

        <Dialog open={companyDialogOpen} onClose={() => setCompanyDialogOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{editingCompany ? 'Editar empresa Omie' : 'Cadastrar empresa Omie'}</DialogTitle>
          <DialogContent dividers>
            <Grid2 container spacing={2} sx={{ pt: 1 }}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField label="Nome" fullWidth value={companyForm.name} onChange={(event) => updateCompanyForm('name', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <TextField label="Alias" fullWidth value={companyForm.alias} onChange={(event) => updateCompanyForm('alias', event.target.value.toUpperCase())} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <TextField
                  label="CNPJ"
                  fullWidth
                  value={maskCNPJ(companyForm.cnpj)}
                  onChange={(event) => updateCompanyForm('cnpj', event.target.value.replace(/\D/g, '').slice(0, 14))}
                  inputProps={{ maxLength: 18 }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControlLabel control={<Checkbox checked={companyForm.active} onChange={(event) => updateCompanyForm('active', event.target.checked)} />} label="Ativa" />
                  <FormControlLabel control={<Checkbox checked={companyForm.canIssueInvoice} onChange={(event) => updateCompanyForm('canIssueInvoice', event.target.checked)} />} label="Pode emitir nota" />
                  <FormControlLabel control={<Checkbox checked={companyForm.canReceivePayment} onChange={(event) => updateCompanyForm('canReceivePayment', event.target.checked)} />} label="Pode receber financeiro" />
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Alert severity={editingCompany?.hasCredentials ? 'success' : 'warning'}>
                  Credenciais configuradas: {editingCompany?.hasCredentials ? 'Sim' : 'Não'}
                </Alert>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField label="App Key" fullWidth value={companyForm.appKey} onChange={(event) => updateCompanyForm('appKey', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField label="App Secret" type="password" fullWidth value={companyForm.appSecret} onChange={(event) => updateCompanyForm('appSecret', event.target.value)} />
              </Grid2>
              {editingCompany && (
                <Grid2 size={{ xs: 12 }}>
                  <FormControlLabel control={<Checkbox checked={companyForm.clearCredentials} onChange={(event) => updateCompanyForm('clearCredentials', event.target.checked)} />} label="Limpar credenciais salvas" />
                </Grid2>
              )}
            </Grid2>
          </DialogContent>
          <DialogActions>
            <Button variant="text" sx={{ width: 120 }} onClick={() => setCompanyDialogOpen(false)}>Cancelar</Button>
            <Button sx={{ width: 120 }} onClick={saveCompany} isLoading={loading}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={bankDialogOpen} onClose={() => setBankDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingBankAccount ? 'Editar conta bancária' : 'Cadastrar conta bancária'}</DialogTitle>
          <DialogContent dividers>
            <Grid2 container spacing={2} sx={{ pt: 1 }}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de pagamento</InputLabel>
                  <Select label="Tipo de pagamento" value={bankAccountForm.paymentType} onChange={(event) => updateBankAccountForm('paymentType', event.target.value as PaymentType)}>
                    {paymentTypes.map(type => <MenuItem key={type} value={type}>{paymentTypeLabels[type]}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Adquirente</InputLabel>
                  <Select label="Adquirente" value={bankAccountForm.acquirer} onChange={(event) => updateBankAccountForm('acquirer', event.target.value as Acquirer | '')}>
                    <MenuItem value="">Sem adquirente</MenuItem>
                    {acquirers.map(acquirer => <MenuItem key={acquirer} value={acquirer}>{acquirerLabels[acquirer]}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField label="Nome" fullWidth value={bankAccountForm.name} onChange={(event) => updateBankAccountForm('name', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 8 }}>
                <TextField label="ID da conta Omie" fullWidth value={bankAccountForm.omieBankAccountId} onChange={(event) => updateBankAccountForm('omieBankAccountId', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField label="Prioridade" type="number" fullWidth value={bankAccountForm.priority} onChange={(event) => updateBankAccountForm('priority', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormControlLabel control={<Checkbox checked={bankAccountForm.active} onChange={(event) => updateBankAccountForm('active', event.target.checked)} />} label="Ativa" />
              </Grid2>
            </Grid2>
          </DialogContent>
          <DialogActions>
            <Button variant="text" sx={{ width: 120 }} onClick={() => setBankDialogOpen(false)}>Cancelar</Button>
            <Button sx={{ width: 120 }} onClick={saveBankAccount} isLoading={loading}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={scenarioDialogOpen} onClose={() => setScenarioDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingFiscalScenario ? 'Editar cenário fiscal' : 'Cadastrar cenário fiscal'}</DialogTitle>
          <DialogContent dividers>
            <Grid2 container spacing={2} sx={{ pt: 1 }}>
              <Grid2 size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de cenário</InputLabel>
                  <Select label="Tipo de cenário" value={fiscalScenarioForm.scenarioType} onChange={(event) => updateFiscalScenarioForm('scenarioType', event.target.value as ScenarioType)}>
                    {scenarioTypes.map(type => <MenuItem key={type} value={type}>{scenarioTypeLabels[type]}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField label="ID fiscal Omie" fullWidth value={fiscalScenarioForm.omieFiscalScenarioId} onChange={(event: ChangeEvent<HTMLInputElement>) => updateFiscalScenarioForm('omieFiscalScenarioId', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormControlLabel control={<Checkbox checked={fiscalScenarioForm.active} onChange={(event) => updateFiscalScenarioForm('active', event.target.checked)} />} label="Ativo" />
              </Grid2>
            </Grid2>
          </DialogContent>
          <DialogActions>
            <Button variant="text" sx={{ width: 120 }} onClick={() => setScenarioDialogOpen(false)}>Cancelar</Button>
            <Button sx={{ width: 120 }} onClick={saveFiscalScenario} isLoading={loading}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={originDialogOpen} onClose={() => setOriginDialogOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{editingOrderOrigin ? 'Editar default por origem' : 'Cadastrar default por origem'}</DialogTitle>
          <DialogContent dividers>
            <Grid2 container spacing={2} sx={{ pt: 1 }}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField label="Nome" fullWidth value={orderOriginForm.name} onChange={(event) => updateOrderOriginForm('name', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Canal</InputLabel>
                  <Select label="Canal" value={orderOriginForm.channel} onChange={(event) => updateOrderOriginForm('channel', event.target.value as Channel)}>
                    <MenuItem value="NUVEMSHOP">NuvemShop</MenuItem>
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Tipo loja</InputLabel>
                  <Select label="Tipo loja" value={orderOriginForm.storeType} onChange={(event) => updateOrderOriginForm('storeType', event.target.value as StoreType)}>
                    <MenuItem value="B2B">B2B</MenuItem>
                    <MenuItem value="B2C">B2C</MenuItem>
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField label="Loja/origem externa" fullWidth value={orderOriginForm.externalStoreId} onChange={(event) => updateOrderOriginForm('externalStoreId', event.target.value)} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Empresa faturamento padrão</InputLabel>
                  <Select label="Empresa faturamento padrão" value={orderOriginForm.defaultBillingCompanyId} onChange={(event) => updateOrderOriginForm('defaultBillingCompanyId', event.target.value as string)}>
                    {activeBillingCompanies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Empresa recebimento padrão</InputLabel>
                  <Select label="Empresa recebimento padrão" value={orderOriginForm.defaultReceivingCompanyId} onChange={(event) => updateOrderOriginForm('defaultReceivingCompanyId', event.target.value as string)}>
                    <MenuItem value="">Padrão</MenuItem>
                    {activeReceivingCompanies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormControlLabel control={<Checkbox checked={orderOriginForm.active} onChange={(event) => updateOrderOriginForm('active', event.target.checked)} />} label="Ativa" />
              </Grid2>
            </Grid2>
          </DialogContent>
          <DialogActions>
            <Button variant="text" sx={{ width: 120 }} onClick={() => setOriginDialogOpen(false)}>Cancelar</Button>
            <Button sx={{ width: 120 }} onClick={saveOrderOrigin} isLoading={loading}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={carrierDialogOpen} onClose={() => setCarrierDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingCarrier ? 'Editar transportadora' : 'Cadastrar transportadora'}</DialogTitle>
          <DialogContent dividers>
            <Grid2 container spacing={2} sx={{ pt: 1 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Nome"
                  fullWidth
                  value={carrierForm.name}
                  onChange={(event) => updateCarrierForm('name', event.target.value)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Código Intelipost"
                  fullWidth
                  value={carrierForm.intelipostCodeId}
                  onChange={(event) => updateCarrierForm('intelipostCodeId', event.target.value)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Logo URL"
                  fullWidth
                  value={carrierForm.logoUrl}
                  onChange={(event) => updateCarrierForm('logoUrl', event.target.value)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <FormControlLabel control={<Checkbox checked={carrierForm.isActive} onChange={(event) => updateCarrierForm('isActive', event.target.checked)} />} label="Ativa" />
              </Grid2>
            </Grid2>
          </DialogContent>
          <DialogActions>
            <Button variant="text" sx={{ width: 120 }} onClick={() => setCarrierDialogOpen(false)}>Cancelar</Button>
            <Button sx={{ width: 120 }} onClick={saveCarrier} isLoading={loading}>Salvar</Button>
          </DialogActions>
        </Dialog>
      </BaseScreen>
    </Can>
  )
}

export { OmieCompaniesAdmin }
